import { useEffect, useRef } from "react"
import { readDay, sunAt } from "./gpu/day.ts"
import { RAMP } from "./gpu/glyphs.ts"

/**
 * The Sun without WebGPU: the same picture drawn on a Canvas 2D context on the
 * CPU — same palette, same glyph ramp, same sunset on scroll. A phone grid is a
 * few thousand cells, so a gentle frame rate is cheap. Static under
 * reduced-motion.
 */

const FPS = 24
const LEVELS = 48
const FONT = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
/** Beyond this distance from the disc edge (in radii) the corona is below the
 * dither-to-black threshold at every dusk — skip the noise entirely. */
const CORONA_CUTOFF = 1.5

const cellFor = (width: number): [number, number] => (width < 640 ? [7, 12] : [9, 16])

// --- noise -------------------------------------------------------------------

const hash = (i: number, j: number): number => {
  const n = Math.sin(i * 127.1 + j * 311.7) * 43758.5453
  return n - Math.floor(n)
}

function noise(x: number, y: number): number {
  const xi = Math.floor(x)
  const yi = Math.floor(y)
  const xf = x - xi
  const yf = y - yi
  const u = xf * xf * (3 - 2 * xf)
  const v = yf * yf * (3 - 2 * yf)
  const a = hash(xi, yi)
  const b = hash(xi + 1, yi)
  const c = hash(xi, yi + 1)
  const d = hash(xi + 1, yi + 1)
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v
}

function fbm(x: number, y: number, octaves: number): number {
  let sum = 0
  let amp = 0.5
  let freq = 1
  for (let i = 0; i < octaves; i++) {
    sum += (noise(x * freq, y * freq) * 2 - 1) * amp
    freq *= 2.1
    amp *= 0.5
  }
  return sum
}

// --- palette (mirrors sun.wgsl) ------------------------------------------------

type Rgb = [number, number, number]
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
]
const smooth = (e0: number, e1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)))
  return t * t * (3 - 2 * t)
}

function palette(v: number, dusk: number): Rgb {
  const ember = mix([0.32, 0.03, 0.0], [0.2, 0.02, 0.0], dusk)
  const orange = mix([0.96, 0.3, 0.04], [0.82, 0.16, 0.03], dusk)
  const gold = mix([1.0, 0.72, 0.16], [0.98, 0.42, 0.09], dusk)
  const core = mix([1.0, 0.97, 0.8], [1.0, 0.74, 0.4], dusk)
  const x = Math.min(1, Math.max(0, v))
  const lo = mix(ember, orange, smooth(0, 0.36, x))
  const hi = mix(gold, core, smooth(0.72, 1, x))
  return mix(lo, hi, smooth(0.36, 0.72, x))
}

function paletteTable(dusk: number): string[] {
  const out: string[] = []
  for (let i = 0; i < LEVELS; i++) {
    const v = (i / (LEVELS - 1)) * 1.15
    const [r, g, b] = palette(v, dusk).map((c) =>
      Math.round(255 * Math.pow(Math.min(1, c * 1.15), 1 / 2.2)),
    )
    out.push(`rgb(${r},${g},${b})`)
  }
  return out
}

// --- glyph atlas ---------------------------------------------------------------
// Every RAMP glyph in every palette colour, rasterised once. A frame is then
// image blits instead of thousands of fillText calls.

function buildAtlas(
  cw: number,
  ch: number,
  dpr: number,
  table: string[],
): HTMLCanvasElement | null {
  const w = Math.round(cw * dpr)
  const h = Math.round(ch * dpr)
  const atlas = document.createElement("canvas")
  atlas.width = w * RAMP.length
  atlas.height = h * LEVELS
  const ctx = atlas.getContext("2d")
  if (ctx === null) return null
  ctx.font = `${Math.round(ch * 0.82 * dpr)}px ${FONT}`
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  for (let level = 0; level < LEVELS; level++) {
    ctx.fillStyle = table[level] ?? "#000"
    const y = (level + 0.52) * h
    for (let g = 1; g < RAMP.length; g++) {
      ctx.fillText(RAMP[g] ?? " ", (g + 0.5) * w, y)
    }
  }
  return atlas
}

// --- one frame -----------------------------------------------------------------

interface Frame {
  readonly ctx: CanvasRenderingContext2D
  readonly cols: number
  readonly rows: number
  readonly cw: number
  readonly ch: number
  readonly dpr: number
  readonly aspect: number
}

function draw(f: Frame, t: number, day: number, atlas: HTMLCanvasElement) {
  const { ctx, cols, rows, cw, ch, dpr, aspect } = f
  const { center, radius, dusk } = sunAt(day, aspect)
  const [cx, cy] = center
  const n = RAMP.length
  const cwD = Math.round(cw * dpr)
  const chD = Math.round(ch * dpr)

  ctx.fillStyle = "#070403"
  ctx.fillRect(0, 0, cols * cw * dpr, rows * ch * dpr)

  const dim = 1 - 0.38 * dusk
  const squash = 1 + 2.4 * dusk

  for (let j = 0; j < rows; j++) {
    const w = (j + 0.5) / rows
    const py = (w - cy) / radius
    for (let i = 0; i < cols; i++) {
      const u = (i + 0.5) / cols
      const px = ((u - cx) * aspect) / radius
      const r = Math.hypot(px, py)
      let v: number
      if (r < 1) {
        const mu = Math.sqrt(1 - r * r)
        const gran = fbm(px * 3.4 + t * 0.05, py * 3.4 - t * 0.03, 3) * 0.5 + 0.5
        v = (0.5 + 0.5 * gran) * (0.64 + 0.36 * mu) * 1.12 + 0.1 * (1 - r) * (1 - r)
      } else {
        const pyc = py * squash
        const rc = Math.hypot(px, pyc)
        const d = rc - 1
        if (d > CORONA_CUTOFF) continue
        const ang = Math.atan2(pyc, px)
        const broad = fbm(ang * 3.2 + t * 0.012, d * 0.55 - t * 0.03, 3) * 0.5 + 0.5
        const rays = Math.pow(broad, 2.2)
        v = Math.exp(-d * 2.3) * (0.06 + 0.94 * rays) * 0.42 + Math.exp(-d * 6.5) * 0.2
      }
      v *= dim
      const h = hash(i + 0.37, j + 0.91)
      if (v < 0.035 * (0.4 + h)) continue
      const idx = Math.min(n - 1, Math.max(0, Math.round(v * (n - 1) + (h - 0.5) * 0.9)))
      if (idx === 0) continue
      const level = Math.min(LEVELS - 1, Math.floor((v / 1.15) * (LEVELS - 1)))
      ctx.drawImage(atlas, idx * cwD, level * chD, cwD, chD, i * cwD, j * chD, cwD, chD)
    }
  }
}

// --- component -----------------------------------------------------------------

export function AsciiSunCanvas({ animate }: { animate: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    if (canvas === null) return
    const ctx = canvas.getContext("2d")
    if (ctx === null) return

    let frame: Frame | null = null
    let invalidateAtlas = () => {}
    const layout = () => {
      const width = Math.max(1, canvas.clientWidth)
      const height = Math.max(1, canvas.clientHeight)
      const coarse = window.matchMedia("(pointer: coarse)").matches
      const dpr = Math.min(coarse ? 1.5 : 2, window.devicePixelRatio || 1)
      const [cw, ch] = cellFor(width)
      const cols = Math.ceil(width / cw)
      const rows = Math.ceil(height / ch)
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      frame = { ctx, cols, rows, cw, ch, dpr, aspect: width / height }
      invalidateAtlas()
    }
    layout()

    let day = readDay()
    let atlasDusk = -1
    let atlas: HTMLCanvasElement | null = null
    invalidateAtlas = () => {
      atlas = null
    }
    const atlasFor = (f: Frame, dusk: number) => {
      const q = Math.round(dusk * 40) / 40
      if (q !== atlasDusk || atlas === null) {
        atlasDusk = q
        atlas = buildAtlas(f.cw, f.ch, f.dpr, paletteTable(q))
      }
      return atlas
    }

    const render = (t: number) => {
      if (frame === null) return
      const a = atlasFor(frame, sunAt(day, frame.aspect).dusk)
      if (a !== null) draw(frame, t, day, a)
    }

    let raf = 0
    let last = performance.now()
    let acc = 0
    const start = last
    const loop = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000)
      last = now
      acc += dt
      day += (readDay() - day) * (1 - Math.exp(-dt * 5))
      if (acc >= 1 / FPS) {
        acc = 0
        render((now - start) / 1000)
      }
      raf = requestAnimationFrame(loop)
    }

    const onResize = () => {
      layout()
      render(animate ? (performance.now() - start) / 1000 : 0)
    }
    window.addEventListener("resize", onResize)

    if (animate) {
      raf = requestAnimationFrame(loop)
    } else {
      // Static: one frame at the current scroll position, redrawn on scroll.
      render(0)
      const onScroll = () => {
        day = readDay()
        render(0)
      }
      window.addEventListener("scroll", onScroll, { passive: true })
      return () => {
        window.removeEventListener("scroll", onScroll)
        window.removeEventListener("resize", onResize)
      }
    }
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener("resize", onResize)
    }
  }, [animate])

  return <canvas ref={ref} className="sun" aria-hidden="true" />
}
